import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useAiReplyDraft,
  useGetGmailMessage,
  useGetGmailStatus,
  useGetMe,
  useListGmailLabels,
  useCreateGmailLabel,
  useReplyToGmailMessage,
  useSendEmail,
  useSetGmailMessageReadState,
  useArchiveGmailMessage,
  useModifyGmailMessageLabels,
  getGetGmailMessageQueryKey,
  getGetGmailStatusQueryKey,
  getListGmailLabelsQueryKey,
  getListGmailMessagesQueryKey,
  type GmailLabel,
  type GmailMessageDetail,
  type SendEmailInput,
  type AiReplyDraftInputTone,
} from "@workspace/api-client-react";
import { REPLY_TONE_VALUES, REPLY_TONE_LABELS, GMAIL_READ_VALUES } from "@/lib/enumValues";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useGmailReconnect,
  GMAIL_SEND_SCOPE,
  GMAIL_MODIFY_SCOPE,
} from "@/hooks/useGmailReconnect";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowLeft,
  Archive,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  MailOpen,
  Plus,
  Reply,
  Send,
  ShieldAlert,
  Sparkles,
  Tag,
} from "lucide-react";
import { apiErrorData, apiErrorMessage, apiErrorStatus } from "@/lib/apiError";
import { VerifiedSenderBadge } from "@/components/VerifiedSenderBadge";
import { FailedVerificationWarning } from "@/components/FailedVerificationWarning";
import type { VerificationDetails } from "@/components/FailedVerificationWarning";
import type { VerificationStatus } from "@/components/VerifiedSenderBadge";
import { useTrustedSenderCheck } from "@/hooks/useTrustedSenderCheck";
import { useReplyDraftPersistence } from "@/lib/replyDraftPersistence";
import { AiRateLimitInline } from "@/components/AiRateLimitInline";
import { useAiRateLimitUpdater } from "@/lib/aiRateLimit";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ReplyTone = AiReplyDraftInputTone;

// Renderer for a single Gmail message at /app/inbox/gmail/:id.
//
// With the gmail.send / gmail.modify scopes granted, exposes inline
// Reply, Mark read/unread, Archive, and a label picker. Without them,
// surfaces a "Reconnect Gmail" prompt directing the user back to the
// inbox CTA.
//
// Independent of those scopes, an "AI Reply Draft" workflow lets the
// user generate a context-aware draft (using FULL thread history,
// signed off with the rep's saved name/title/phone). When the user has
// gmail.send, they can route the AI draft into the inline reply
// composer with a single click; otherwise they fall back to copying
// the draft or opening Gmail's Compose UI prefilled with it.
export default function GmailDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const id = params.id ?? "";
  const queryClient = useQueryClient();

  const messageQuery = useGetGmailMessage(id, {
    query: {
      queryKey: getGetGmailMessageQueryKey(id),
      enabled: id.length > 0,
    },
  });

  // Status drives what actions are exposed: mark/archive/labels need
  // gmail.modify, reply needs gmail.send. Connecting the dots in one
  // place keeps the disabled / hidden logic consistent.
  const statusQuery = useGetGmailStatus({
    query: { queryKey: getGetGmailStatusQueryKey() },
  });
  const grantedScopes = statusQuery.data?.scopes ?? [];
  const canModify = grantedScopes.includes(GMAIL_MODIFY_SCOPE);
  const canSend = grantedScopes.includes(GMAIL_SEND_SCOPE);
  // True only once we have a status payload back AND the gmail.send
  // scope is missing. Treating "status hasn't loaded yet" as
  // "missing" would briefly flash the re-grant banner on every page
  // load, so we explicitly gate on `data` being present.
  const sendScopeMissing =
    statusQuery.data !== undefined &&
    statusQuery.data.connected &&
    !canSend;

  // Shared Clerk reauthorize flow (also powers the Inbox CTA). The
  // hook navigates away to Google's consent screen; on return the
  // browser lands back on this page with the persisted reply draft
  // intact thanks to `useReplyDraftPersistence` above.
  const gmailReconnect = useGmailReconnect();

  const aiReplyMutation = useAiReplyDraft();
  const aiRateLimit = useAiRateLimitUpdater();

  // Inline AI-draft panel state. Lives below the message body. Until
  // the user asks for a draft we don't render the panel at all — keep
  // the read-only experience clean for users who just want to read.
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftAi, setDraftAi] = useState(false);
  const [tone, setTone] = useState<ReplyTone>("auto");
  const [aiPending, setAiPending] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [showRepDefaultsNudge, setShowRepDefaultsNudge] = useState(false);

  // Sandbox the html body inside an iframe with srcDoc + sandbox=""
  // so any inline scripts / event handlers from the email can't reach
  // the host page.
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const htmlBody = messageQuery.data?.htmlBody ?? null;
  const textBody = messageQuery.data?.textBody ?? null;
  const srcDoc = useMemo(() => {
    if (!htmlBody) return null;
    return `<!doctype html><html><head><meta charset="utf-8"><style>
      body { margin: 0; padding: 0; font-family: -apple-system, system-ui, sans-serif; font-size: 14px; color: #111; word-wrap: break-word; }
      img, table { max-width: 100%; }
    </style></head><body>${htmlBody}</body></html>`;
  }, [htmlBody]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !srcDoc) return;
    const onLoad = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.body.scrollHeight,
          doc.documentElement?.scrollHeight ?? 0,
        );
        iframe.style.height = `${h + 20}px`;
      } catch {
        iframe.style.height = "60vh";
      }
    };
    iframe.addEventListener("load", onLoad);
    return () => {
      iframe.removeEventListener("load", onLoad);
    };
  }, [srcDoc]);

  // Inline reply composer state.
  //
  // When the user has the gmail.send scope, replies route through
  // Gmail's `messages.send` endpoint (via `/api/inbox/gmail/messages/:id/reply`)
  // so they appear in the user's Gmail Sent folder and thread natively.
  // Without gmail.send, falls back to MailCraft's own `/api/send`
  // (Resend on a verified domain) with RFC-2822 threading headers and
  // a Reply-To override pointing at the user's Gmail address.
  const [showReply, setShowReply] = useState(false);
  const [replyTo, setReplyTo] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  // Tracks whether the body in the inline composer originated from
  // the AI draft (via `handleUseAsReply`) vs the user typing it
  // themselves. Persisted alongside the draft so reopening the thread
  // can re-display the "AI draft" pill — and so we don't claim AI
  // authorship of a hand-written reply.
  const [replyIsAiDrafted, setReplyIsAiDrafted] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  // Set to true when a send attempt itself returns 412
  // gmail_scope_missing for gmail.send. Distinct from `sendScopeMissing`
  // (derived from /status pre-flight) because /status may be stale —
  // a token that lost its scope mid-session won't be reflected until
  // the next refetch. Either signal surfaces the same in-place
  // re-grant banner and the user's draft stays put.
  const [sendFailedScopeMissing, setSendFailedScopeMissing] = useState(false);
  const sendMutation = useSendEmail();
  const gmailReplyMutation = useReplyToGmailMessage();
  const replySending = sendMutation.isPending || gmailReplyMutation.isPending;

  // Persist the inline reply composer to the server so a half-typed
  // reply survives a page refresh / navigation. Keyed off the visible
  // Gmail message id (matching the URL) rather than the conversation
  // thread id so opening a different message in the same Gmail thread
  // does NOT inherit a stranger's draft.
  const replyDraft = useReplyDraftPersistence({
    kind: "gmail",
    ref: id,
    enabled: id.length > 0,
    open: showReply,
    subject: replySubject,
    body: replyBody,
    isAiDrafted: replyIsAiDrafted,
    onRestore: (draft) => {
      if (!draft) return;
      if (draft.subject.length === 0 && draft.body.length === 0) return;
      // We restore subject + body but NOT `replyTo` — that always
      // points at the inbound sender, which the prefill effect picks
      // up from the freshly-loaded `messageQuery.data`. Restoring
      // `replyTo` from a stale draft would risk addressing the reply
      // to whoever the user happened to have in the field last time.
      setReplySubject(draft.subject);
      setReplyBody(draft.body);
      setReplyIsAiDrafted(draft.isAiDrafted);
      setShowReply(true);
    },
  });

  const setReadStateMutation = useSetGmailMessageReadState();
  const archiveMutation = useArchiveGmailMessage();
  const labelsMutation = useModifyGmailMessageLabels();

  // User preference: "auto-mark Gmail messages read on view". Off by
  // default. Resolved through the same /me endpoint that powers the
  // sending defaults card on the Brand page. We gate the auto-mark on
  // (a) the user opted in, (b) the message exists and is currently
  // unread, and (c) the granted Gmail token actually carries the
  // gmail.modify scope (otherwise the modify call would 412).
  const meQuery = useGetMe();
  const markOnView = meQuery.data?.gmailMarkReadOnView === true;
  const autoMarkAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    const m = messageQuery.data;
    if (!m) return;
    if (!m.isUnread) return;
    if (!markOnView) return;
    if (!canModify) return;
    // Guard against the effect re-running (e.g. a refetch flipping
    // `isUnread` momentarily) by tracking which message id we've
    // already kicked the auto-mark for. The optimistic update flips
    // `isUnread` to false synchronously, so a re-render of the same
    // id is a no-op anyway, but this also prevents a second attempt
    // after a rolled-back failure.
    if (autoMarkAttemptedRef.current === m.id) return;
    autoMarkAttemptedRef.current = m.id;
    queryClient.setQueryData<GmailMessageDetail>(
      getGetGmailMessageQueryKey(id),
      (prev) => (prev ? { ...prev, isUnread: false } : prev),
    );
    setReadStateMutation
      .mutateAsync({ id: m.id, data: { read: true } })
      .then(() => {
        invalidateGmailLists(queryClient);
      })
      .catch(() => {
        // Roll back on failure but keep the attempt logged so we don't
        // hammer the API. The explicit Mark read button stays available.
        queryClient.setQueryData<GmailMessageDetail>(
          getGetGmailMessageQueryKey(id),
          (prev) => (prev ? { ...prev, isUnread: true } : prev),
        );
        // Silent — auto-mark is a nicety. The reconnect banner already
        // surfaces the underlying scope/token issue if any.
      });
  }, [
    messageQuery.data,
    markOnView,
    canModify,
    id,
    queryClient,
    setReadStateMutation,
  ]);

  const onMarkToggle = async () => {
    const m = messageQuery.data;
    if (!m) return;
    const becomingRead = m.isUnread;
    queryClient.setQueryData<GmailMessageDetail>(
      getGetGmailMessageQueryKey(id),
      (prev) => (prev ? { ...prev, isUnread: !becomingRead } : prev),
    );
    try {
      await setReadStateMutation.mutateAsync({
        id: m.id,
        data: { read: becomingRead },
      });
      invalidateGmailLists(queryClient);
    } catch (err) {
      // Roll back on failure.
      queryClient.setQueryData<GmailMessageDetail>(
        getGetGmailMessageQueryKey(id),
        (prev) => (prev ? { ...prev, isUnread: becomingRead } : prev),
      );
      handleScopeError(err, "Couldn't update read state");
    }
  };

  const onArchive = async () => {
    const m = messageQuery.data;
    if (!m) return;
    try {
      await archiveMutation.mutateAsync({ id: m.id });
      toast.success("Archived");
      invalidateGmailLists(queryClient);
      setLocation("/app/inbox");
    } catch (err) {
      handleScopeError(err, "Couldn't archive message");
    }
  };

  // Prefill the composer the moment the user opens it. We do this in
  // an effect (not in the click handler) so AI-draft → "Use as reply"
  // can also reuse the same prefill path: it sets `replyBody` then
  // flips `showReply` to true, and the effect leaves a non-empty
  // body alone.
  useEffect(() => {
    const m = messageQuery.data;
    if (!showReply || !m) return;
    if (replyTo === "") setReplyTo(m.fromAddress);
    if (replySubject === "") setReplySubject(buildReplySubject(m.subject));
    if (replyBody === "") setReplyBody(buildQuotedReplyBody(m));
    // Intentionally only depends on `showReply` + the message id so a
    // re-fetch of the same message doesn't clobber what the user typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReply, messageQuery.data?.id]);

  const resetReplyComposer = () => {
    setShowReply(false);
    setReplyTo("");
    setReplySubject("");
    setReplyBody("");
    setReplyIsAiDrafted(false);
    setReplyError(null);
  };

  // Explicit "Discard draft" — clears the saved row on the server AND
  // wipes the inline composer so reopening starts from the
  // default quoted-body prefill again. Cancel (above) only collapses
  // the panel and deliberately leaves the saved draft alone.
  const discardReplyDraft = async () => {
    await replyDraft.clearDraft();
    resetReplyComposer();
    toast.success("Draft discarded");
  };

  const onSendReply = async () => {
    const m = messageQuery.data;
    if (!m) return;
    setReplyError(null);
    setSendFailedScopeMissing(false);
    const to = replyTo.trim();
    const subject = replySubject.trim();
    const body = replyBody;
    if (!canSend && to.length === 0) {
      setReplyError("To address is required");
      return;
    }
    if (body.trim().length === 0) {
      setReplyError("Reply body is required");
      return;
    }
    const replyHtml = `<pre style="font-family: -apple-system, system-ui, sans-serif; white-space: pre-wrap; padding: 16px; line-height: 1.5;">${body
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")}</pre>`;

    const onSuccess = () => {
      toast.success("Reply sent");
      void replyDraft.clearDraft();
      resetReplyComposer();
      queryClient.invalidateQueries({
        queryKey: getGetGmailMessageQueryKey(id),
      });
    };

    const handleSendError = (err: unknown) => {
      const status = apiErrorStatus(err);
      const data = apiErrorData<{ reason?: string; missingScope?: string }>(
        err,
      );
      if (
        status === 412 &&
        data?.reason === "gmail_scope_missing" &&
        data?.missingScope === GMAIL_SEND_SCOPE
      ) {
        setSendFailedScopeMissing(true);
        setReplyError(null);
        statusQuery.refetch();
        return;
      }
      const msg = apiErrorMessage(err, "Couldn't send reply");
      setReplyError(msg);
    };

    if (canSend) {
      try {
        await gmailReplyMutation.mutateAsync({
          id: m.id,
          data: {
            text: body,
            html: replyHtml,
            ...(subject.length > 0 ? { subject } : {}),
          },
        });
        onSuccess();
      } catch (err) {
        handleSendError(err);
      }
    } else {
      const gmailAddr = statusQuery.data?.gmailAddress ?? null;
      const inReplyTo = m.messageIdHeader ?? null;
      const references = buildClientReferencesHeader(
        m.referencesHeader ?? null,
        m.messageIdHeader ?? null,
      );
      const payload: SendEmailInput = {
        templateId: null,
        to,
        subject,
        text: body,
        html: replyHtml,
        ...(gmailAddr ? { replyToOverride: gmailAddr } : {}),
        ...(inReplyTo ? { inReplyTo } : {}),
        ...(references ? { references } : {}),
      };
      try {
        await sendMutation.mutateAsync({ data: payload });
        onSuccess();
      } catch (err) {
        handleSendError(err);
      }
    }
  };

  const runAiReplyDraft = async () => {
    setAiPending(true);
    try {
      const draft = await aiReplyMutation.mutateAsync({
        data: {
          gmailMessageId: id,
          ...(tone !== "auto" ? { tone } : {}),
        },
      });
      aiRateLimit.updateFromResponse({
        rateLimitRemaining: draft.rateLimitRemaining,
        rateLimitMax: draft.rateLimitMax,
        rateLimitResetAt: new Date(draft.rateLimitResetAt).toISOString(),
      });
      setDraftSubject(draft.subject);
      setDraftBody(draft.body);
      setDraftAi(true);
      setShowRepDefaultsNudge(draft.repDefaultsMissing === true);
      setDraftOpen(true);
      setTimeout(() => {
        document
          .getElementById("gmail-draft-panel")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    } catch (e) {
      aiRateLimit.updateFromError(e);
      const status = apiErrorStatus(e);
      if (status === 429) {
        toast.error(
          `AI rate limit reached. ${apiErrorMessage(e, "Try again later.")}`,
        );
      } else if (status === 412) {
        toast.error(
          "Your Gmail connection needs to be re-authorized before AI drafts can read the thread.",
        );
      } else {
        toast.error(apiErrorMessage(e, "AI reply failed"));
      }
    } finally {
      setAiPending(false);
    }
  };

  const handleAiReply = () => {
    const hasUserContent =
      draftOpen &&
      (draftBody.trim().length > 0 || draftSubject.trim().length > 0);
    if (hasUserContent) {
      setConfirmRegenerate(true);
      return;
    }
    void runAiReplyDraft();
  };

  const handleCopy = async () => {
    const text =
      (draftSubject.trim() ? `Subject: ${draftSubject.trim()}\n\n` : "") +
      draftBody;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Draft copied to clipboard");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  // Bridge from the AI draft into the inline reply composer. Available
  // to every Gmail-connected user. We port the AI's subject AND body
  // into the inline composer.
  const handleUseAsReply = () => {
    setReplyError(null);
    const m = messageQuery.data;
    if (m) setReplyTo(m.fromAddress);
    setReplySubject(draftSubject || (m ? buildReplySubject(m.subject) : ""));
    setReplyBody(draftBody);
    // Tag the inline composer as AI-drafted so the persisted draft
    // (and the panel header pill) reflect it. Cleared if the user
    // subsequently edits the body — see the textarea onChange below.
    setReplyIsAiDrafted(draftAi);
    setShowReply(true);
    setDraftOpen(false);
    setDraftAi(false);
    setShowRepDefaultsNudge(false);
    setTimeout(() => {
      document
        .querySelector('[data-testid="gmail-detail-reply-body"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  if (messageQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (messageQuery.isError || !messageQuery.data) {
    const status = apiErrorStatus(messageQuery.error);
    const data = apiErrorData<{ reason?: string }>(messageQuery.error);
    const reason = data?.reason;
    const explanation =
      status === 412 && reason
        ? reason === "gmail_token_revoked" || reason === "gmail_scope_missing"
          ? "Your Gmail connection was revoked or is missing the required scope. Reconnect it from the Inbox page."
          : "Gmail is not currently available."
        : status === 404
          ? "This Gmail message no longer exists."
          : apiErrorMessage(messageQuery.error, "Could not load this message.");
    return (
      <div className="flex-1 flex flex-col h-screen overflow-hidden bg-background">
        <div className="h-16 px-6 border-b border-border flex items-center gap-3 bg-card">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/app/inbox")}
            data-testid="gmail-detail-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Inbox
          </Button>
        </div>
        <div className="p-12 text-center text-muted-foreground">
          <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium" data-testid="gmail-detail-error">
            {explanation}
          </p>
        </div>
      </div>
    );
  }

  const m = messageQuery.data;
  const receivedAt = new Date(m.receivedAt);
  const receivedLabel = Number.isFinite(receivedAt.getTime())
    ? receivedAt.toLocaleString()
    : m.receivedAt;
  // The reconnect banner fires when inbox actions (mark / archive /
  // labels) are unavailable due to the gmail.modify scope missing.
  const needsReconnect = !canModify;

  // Deep-link into Gmail's web Compose UI pre-filled with the AI
  // draft so the user can review and send from Gmail itself. Gmail's
  // /mail/?view=cm endpoint accepts to/su/body via querystring.
  const gmailComposeHref = (() => {
    const u = new URL("https://mail.google.com/mail/");
    u.searchParams.set("view", "cm");
    u.searchParams.set("fs", "1");
    if (m.fromAddress) u.searchParams.set("to", m.fromAddress);
    if (draftSubject.trim()) u.searchParams.set("su", draftSubject.trim());
    if (draftBody.trim()) u.searchParams.set("body", draftBody);
    return u.toString();
  })();

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden bg-background">
      <div className="h-16 px-6 border-b border-border flex items-center justify-between bg-card shrink-0 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/app/inbox")}
            data-testid="gmail-detail-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Inbox
          </Button>
          <h1
            className="font-bold text-lg truncate"
            data-testid="gmail-detail-subject"
          >
            {m.subject || "(no subject)"}
          </h1>
          <Badge variant="outline" className="shrink-0">
            Gmail
          </Badge>
          {m.isUnread && (
            <Badge variant="default" className="shrink-0">
              Unread
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canModify && (
            <Button
              variant="outline"
              size="sm"
              onClick={onMarkToggle}
              disabled={setReadStateMutation.isPending}
              data-testid="gmail-detail-mark"
            >
              {m.isUnread ? (
                <>
                  <MailOpen className="w-3.5 h-3.5 mr-1.5" />
                  Mark read
                </>
              ) : (
                <>
                  <Mail className="w-3.5 h-3.5 mr-1.5" />
                  Mark unread
                </>
              )}
            </Button>
          )}
          {canModify && (
            <LabelPicker
              currentLabelIds={m.labelIds}
              mutating={labelsMutation.isPending}
              onMutate={async (add, remove) => {
                try {
                  const result = await labelsMutation.mutateAsync({
                    id: m.id,
                    data: { add, remove },
                  });
                  // Patch the open message detail with the new
                  // labelIds the server returned, then invalidate
                  // so the next refetch reconciles. Without this the
                  // picker would reopen with stale `currentLabelIds`
                  // as its diff baseline and a follow-up add/remove
                  // pass would compute the wrong diff.
                  queryClient.setQueryData<GmailMessageDetail>(
                    getGetGmailMessageQueryKey(m.id),
                    (prev) =>
                      prev
                        ? { ...prev, labelIds: result.labelIds }
                        : prev,
                  );
                  invalidateGmailLists(queryClient);
                  await queryClient.invalidateQueries({
                    queryKey: getGetGmailMessageQueryKey(m.id),
                  });
                  toast.success("Labels updated");
                } catch (err) {
                  handleScopeError(err, "Couldn't update labels");
                  throw err;
                }
              }}
            />
          )}
          {canModify && (
            <Button
              variant="outline"
              size="sm"
              onClick={onArchive}
              disabled={archiveMutation.isPending}
              data-testid="gmail-detail-archive"
            >
              <Archive className="w-3.5 h-3.5 mr-1.5" />
              Archive
            </Button>
          )}
          <Select
            value={tone}
            onValueChange={(v) => setTone(v as ReplyTone)}
          >
            <SelectTrigger
              className="h-8 w-[120px] text-xs"
              data-testid="gmail-detail-ai-tone"
              title="Tone for the AI-drafted reply"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPLY_TONE_VALUES.map((v) => (
                <SelectItem key={v} value={v}>{REPLY_TONE_LABELS[v]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAiReply}
            disabled={aiPending}
            data-testid="gmail-detail-ai-reply"
          >
            {aiPending ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-1.5" />
            )}
            Draft reply
          </Button>
          <AiRateLimitInline />
          <Button
            size="sm"
            onClick={() => setShowReply((v) => !v)}
            data-testid="gmail-detail-reply"
          >
            <Reply className="w-3.5 h-3.5 mr-1.5" />
            Reply
          </Button>
        </div>
      </div>

      <AlertDialog
        open={confirmRegenerate}
        onOpenChange={setConfirmRegenerate}
      >
        <AlertDialogContent data-testid="gmail-detail-ai-regenerate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace your draft?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsent text in the draft panel. Generating a new AI
              draft will overwrite the subject and body.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my draft</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmRegenerate(false);
                void runAiReplyDraft();
              }}
              data-testid="gmail-detail-ai-regenerate-confirm-yes"
            >
              Replace with AI draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
          {needsReconnect && (
            <div
              className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 flex items-start gap-2"
              data-testid="gmail-detail-reconnect-banner"
            >
              <ShieldAlert className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 text-xs">
                <div className="font-medium text-foreground">
                  Reconnect Gmail to enable inbox actions
                </div>
                <p className="text-muted-foreground mt-0.5">
                  Your Gmail connection is read-only. Replies still
                  work — they go out from your verified MailCraft
                  domain with Reply-To set to your Gmail address.
                  Reconnect from the Inbox page to also mark messages,
                  archive, or manage labels.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLocation("/app/inbox")}
                data-testid="gmail-detail-reconnect-link"
              >
                Open Inbox
              </Button>
            </div>
          )}
          <div
            className="rounded-lg border border-border bg-card p-4 space-y-2"
            data-testid="gmail-detail-headers"
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold inline-flex items-center gap-1.5">
                {m.fromName || m.fromAddress}
              </span>
              <VerifiedSenderBadge
                verificationStatus={m.verificationStatus ?? null}
                testIdSuffix="-gmail-detail"
              />
              <GmailDetailFailedVerification
                fromAddress={m.fromAddress}
                verificationStatus={m.verificationStatus ?? null}
                verificationDetails={m.verificationDetails ?? null}
              />
              {m.fromName && (
                <span className="text-xs text-muted-foreground font-mono">
                  &lt;{m.fromAddress}&gt;
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {receivedLabel}
              </span>
            </div>
            {m.toAddress && (
              <div className="text-xs text-muted-foreground">
                To: <span className="font-mono">{m.toAddress}</span>
              </div>
            )}
          </div>
          {showReply && (
            <div
              className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-3"
              data-testid="gmail-detail-reply-composer"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  {canSend ? (
                    <>
                      Sending from your Gmail account{" "}
                      <span className="font-mono">
                        {statusQuery.data?.gmailAddress ?? "your Gmail address"}
                      </span>
                      . The reply will appear in your Gmail Sent folder.
                    </>
                  ) : (
                    <>
                      Sending from your verified MailCraft domain. Replies
                      land back in Gmail via Reply-To{" "}
                      <span className="font-mono">
                        {statusQuery.data?.gmailAddress ?? "your Gmail address"}
                      </span>
                      .
                    </>
                  )}
                </div>
                {replyIsAiDrafted && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] uppercase tracking-wider shrink-0"
                    data-testid="gmail-detail-reply-ai-pill"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI draft
                  </Badge>
                )}
              </div>
              {(sendScopeMissing || sendFailedScopeMissing) && (
                // In-place "Re-grant Gmail send access" banner. Shown
                // when /api/inbox/gmail/status reports the gmail.send
                // scope is missing (pre-flight) OR a previous send
                // attempt returned 412 gmail_scope_missing for
                // gmail.send. The user's draft (To / Subject / Body)
                // stays in place — clicking the button kicks off
                // Clerk's reauthorize flow with redirectUrl set to
                // this page, and `useReplyDraftPersistence` rehydrates
                // the composer when Google bounces the browser back.
                <div
                  className="flex items-start gap-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-3"
                  data-testid="gmail-detail-reply-regrant"
                >
                  <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="font-medium text-foreground">
                      {sendFailedScopeMissing
                        ? "Gmail send permission was revoked mid-session"
                        : "Re-grant Gmail send access"}
                    </div>
                    <p className="text-muted-foreground mt-0.5">
                      {sendFailedScopeMissing
                        ? "Google rejected the send because the gmail.send scope is no longer granted. Re-authorize to send this reply through your Gmail account — your draft stays right here."
                        : "Sending replies through your own Gmail account needs the gmail.send scope. Re-authorize to enable it — your draft stays right here."}
                    </p>
                    {gmailReconnect.error && (
                      <p
                        className="text-destructive mt-1.5"
                        data-testid="gmail-detail-reply-regrant-error"
                      >
                        {gmailReconnect.error}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void gmailReconnect.reconnect();
                    }}
                    disabled={gmailReconnect.isPending}
                    data-testid="gmail-detail-reply-regrant-button"
                  >
                    {gmailReconnect.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : null}
                    Re-grant Gmail send access
                  </Button>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="gmail-reply-to" className="text-xs">
                  To
                </Label>
                <Input
                  id="gmail-reply-to"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  disabled={canSend}
                  data-testid="gmail-detail-reply-to"
                />
                {canSend && (
                  <p className="text-[11px] text-muted-foreground">
                    Recipient is set by Gmail threading and cannot be changed.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gmail-reply-subject" className="text-xs">
                  Subject
                </Label>
                <Input
                  id="gmail-reply-subject"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  data-testid="gmail-detail-reply-subject"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gmail-reply-body" className="text-xs">
                  Message
                </Label>
                <Textarea
                  id="gmail-reply-body"
                  rows={10}
                  value={replyBody}
                  onChange={(e) => {
                    setReplyBody(e.target.value);
                    // Hand-edits demote the body from "AI draft" to
                    // "user-authored" so the persisted draft and the
                    // panel header pill don't keep claiming AI
                    // authorship of the user's own typing.
                    if (replyIsAiDrafted) setReplyIsAiDrafted(false);
                  }}
                  placeholder="Write your reply…"
                  data-testid="gmail-detail-reply-body"
                />
              </div>
              {replyError && (
                <div
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                  data-testid="gmail-detail-reply-error"
                >
                  {replyError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={discardReplyDraft}
                  disabled={replySending}
                  data-testid="gmail-detail-reply-discard"
                >
                  Discard draft
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowReply(false);
                    setReplyError(null);
                  }}
                  disabled={replySending}
                  data-testid="gmail-detail-reply-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={onSendReply}
                  disabled={
                    replySending ||
                    replyBody.trim().length === 0 ||
                    (!canSend && replyTo.trim().length === 0)
                  }
                  data-testid="gmail-detail-reply-send"
                >
                  {replySending ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Send
                </Button>
              </div>
            </div>
          )}
          <div
            className="rounded-lg border border-border bg-card p-4"
            data-testid="gmail-detail-body"
          >
            {srcDoc ? (
              <iframe
                ref={iframeRef}
                title="Gmail message body"
                sandbox=""
                srcDoc={srcDoc}
                className="w-full border-0"
                style={{ minHeight: "200px" }}
                data-testid="gmail-detail-iframe"
              />
            ) : textBody ? (
              <pre
                className="whitespace-pre-wrap font-sans text-sm text-foreground"
                data-testid="gmail-detail-text"
              >
                {textBody}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {m.snippet || "(no body content)"}
              </p>
            )}
          </div>

          {draftOpen && (
            <div
              id="gmail-draft-panel"
              data-testid="gmail-draft-panel"
              className="bg-card border border-border rounded-lg p-5 space-y-4"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Reply className="w-4 h-4 text-primary" />
                  AI-drafted reply
                  {draftAi && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] uppercase tracking-wider"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI draft
                    </Badge>
                  )}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDraftOpen(false);
                    setDraftAi(false);
                    setShowRepDefaultsNudge(false);
                  }}
                  data-testid="gmail-draft-cancel"
                >
                  Cancel
                </Button>
              </div>

              <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-2 text-xs items-center">
                <div className="text-muted-foreground">To</div>
                <div className="font-mono">{m.fromAddress}</div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="gmail-draft-subject" className="text-xs">
                  Subject
                </Label>
                <Input
                  id="gmail-draft-subject"
                  value={draftSubject}
                  onChange={(e) => setDraftSubject(e.target.value)}
                  data-testid="gmail-draft-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gmail-draft-body" className="text-xs">
                  Message
                </Label>
                <Textarea
                  id="gmail-draft-body"
                  value={draftBody}
                  onChange={(e) => {
                    setDraftBody(e.target.value);
                    if (draftAi) setDraftAi(false);
                  }}
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="gmail-draft-body"
                />
                {showRepDefaultsNudge && draftAi && (
                  <p
                    className="text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1"
                    data-testid="gmail-draft-rep-defaults-nudge"
                  >
                    <span>
                      Your AI draft is missing a personal sign-off.
                    </span>
                    <Link
                      href="/app/representative"
                      className="underline hover:no-underline"
                    >
                      Add your name, title, and phone →
                    </Link>
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  data-testid="gmail-draft-copy"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  data-testid="gmail-draft-open-in-gmail"
                >
                  <a
                    href={gmailComposeHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Open in Gmail
                  </a>
                </Button>
                <Button
                  size="sm"
                  onClick={handleUseAsReply}
                  disabled={draftBody.trim().length === 0}
                  data-testid="gmail-draft-use-as-reply"
                >
                  <Reply className="w-3.5 h-3.5 mr-1.5" />
                  Use as reply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Label picker ───────────────────────────────────────────────────────
//
// Popover with a checklist of the user's Gmail labels. We only fetch
// the label list when the popover is opened (no need to pull the full
// list on every detail page load); on apply we diff the working
// selection against the labels that were applied to the message at
// open time and translate the difference into add/remove arrays.
//
// An inline "Create new label" affordance lets users spin up a new
// label in the moment via POST /inbox/gmail/labels — the new label is
// pre-checked in the working selection so the user can apply it on the
// same trip.
function LabelPicker({
  currentLabelIds,
  mutating,
  onMutate,
}: {
  currentLabelIds: string[];
  mutating: boolean;
  onMutate: (add: string[], remove: string[]) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const labelsQuery = useListGmailLabels({
    query: {
      queryKey: getListGmailLabelsQueryKey(),
      enabled: open,
    },
  });
  const createLabelMutation = useCreateGmailLabel();
  const labels = labelsQuery.data?.labels ?? [];
  const userLabels = labels.filter((l) => l.type === "user");

  // Snapshot of the labels that were applied to the message at the
  // moment the popover opened — used as the diff baseline so unchecking
  // a previously-applied label maps to a `remove`. Captured into a ref
  // on each open so a background refetch of `currentLabelIds` while
  // the popover is open does not reset the user's working selection
  // mid-edit.
  const initialIdsRef = useRef<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(currentLabelIds),
  );
  const [newLabelName, setNewLabelName] = useState("");

  // Re-snapshot the baseline + working selection whenever the popover
  // transitions from closed to open. Inside an open session we keep
  // the snapshot stable even if `currentLabelIds` changes underneath,
  // so a server refetch can't clobber the user's in-progress checks.
  useEffect(() => {
    if (open) {
      initialIdsRef.current = new Set(currentLabelIds);
      setSelected(new Set(currentLabelIds));
      setNewLabelName("");
    }
    // We deliberately depend on `open` only — re-running this on
    // `currentLabelIds` would defeat the "stable while open"
    // guarantee.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const initialIds = initialIdsRef.current;

  const userLabelIds = useMemo(
    () => new Set(userLabels.map((l) => l.id)),
    [userLabels],
  );

  // Compute the diff between the working set and the message's current
  // labels. We restrict the `remove` side to labels we actually know
  // about (user-type) so the picker can never accidentally strip a
  // system label like INBOX.
  const add: string[] = [];
  const remove: string[] = [];
  for (const id of selected) {
    if (!initialIds.has(id)) add.push(id);
  }
  for (const id of initialIds) {
    if (!selected.has(id) && userLabelIds.has(id)) remove.push(id);
  }
  const hasChanges = add.length > 0 || remove.length > 0;

  const apply = async () => {
    if (!hasChanges) {
      setOpen(false);
      return;
    }
    try {
      await onMutate(add, remove);
      setOpen(false);
    } catch {
      // toast already raised by caller
    }
  };

  const createLabel = async () => {
    const name = newLabelName.trim();
    if (name.length === 0) return;
    try {
      const created = await createLabelMutation.mutateAsync({
        data: { name },
      });
      // Refresh the picker's label list and pre-check the new label so
      // the user can apply it on the same trip.
      await queryClient.invalidateQueries({
        queryKey: getListGmailLabelsQueryKey(),
      });
      setSelected((prev) => {
        const next = new Set(prev);
        next.add(created.id);
        return next;
      });
      setNewLabelName("");
      toast.success(`Created label "${created.name}"`);
    } catch (err) {
      const status = apiErrorStatus(err);
      if (status === 409) {
        toast.error(`A label named "${name}" already exists.`);
        return;
      }
      handleScopeError(err, "Couldn't create label");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="gmail-detail-labels-trigger"
        >
          <Tag className="w-3.5 h-3.5 mr-1.5" />
          Labels
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="end"
        data-testid="gmail-detail-labels-popover"
      >
        <div className="px-3 py-2 border-b border-border text-xs font-medium text-muted-foreground">
          Apply labels to this message
        </div>
        <div className="max-h-64 overflow-auto py-1">
          {labelsQuery.isLoading ? (
            <div className="p-4 flex justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : userLabels.length === 0 ? (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">
              No labels yet. Create one below.
            </div>
          ) : (
            userLabels.map((l: GmailLabel) => {
              const checked = selected.has(l.id);
              return (
                <label
                  key={l.id}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer text-sm"
                  data-testid={`gmail-label-option-${l.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (c) next.add(l.id);
                        else next.delete(l.id);
                        return next;
                      });
                    }}
                  />
                  <span className="truncate">{l.name}</span>
                </label>
              );
            })
          )}
        </div>
        <div className="px-3 py-2 border-t border-border space-y-2">
          <div className="text-xs font-medium text-muted-foreground">
            Create new label
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
              placeholder="e.g. Customer Replies"
              maxLength={225}
              className="h-8 text-sm"
              disabled={createLabelMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void createLabel();
                }
              }}
              data-testid="gmail-detail-labels-new-name"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void createLabel()}
              disabled={
                createLabelMutation.isPending || newLabelName.trim().length === 0
              }
              data-testid="gmail-detail-labels-new-create"
            >
              {createLabelMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
            </Button>
          </div>
        </div>
        <div className="px-3 py-2 border-t border-border flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={mutating}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={apply}
            disabled={mutating || !hasChanges}
            data-testid="gmail-detail-labels-apply"
          >
            {mutating ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : null}
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function handleScopeError(err: unknown, fallback: string): void {
  const data = apiErrorData<{ reason?: string }>(err);
  if (data?.reason === "gmail_scope_missing") {
    toast.error(
      "Reconnect Gmail to enable this action — the required scope is missing.",
    );
    return;
  }
  if (data?.reason === "gmail_token_revoked") {
    toast.error(
      "Your Gmail connection was revoked. Reconnect from the Inbox page.",
    );
    return;
  }
  toast.error(apiErrorMessage(err, fallback));
}

function invalidateGmailLists(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  // Invalidate every variant of the messages list so the inbox
  // reflects the change regardless of the active read filter.
  for (const read of GMAIL_READ_VALUES) {
    queryClient.invalidateQueries({
      queryKey: getListGmailMessagesQueryKey({ read, limit: 50 }),
    });
  }
}

// Add a single "Re: " prefix unless the inbound subject already has
// one (case-insensitive, tolerant of any leading whitespace). Mirrors
// what Gmail and other clients do.
function buildReplySubject(originalSubject: string | null | undefined): string {
  const subj = (originalSubject ?? "").trim();
  if (subj.length === 0) return "Re:";
  if (/^re:\s/i.test(subj) || /^re:$/i.test(subj)) return subj;
  return `Re: ${subj}`;
}

// Append the inbound's Message-ID onto its existing References chain
// to produce the new outbound References header. Per RFC 5322 §3.6.4
// the chain grows monotonically and the new In-Reply-To is the
// last element. Returns null when there's nothing to thread on.
function buildClientReferencesHeader(
  existingReferences: string | null,
  inboundMessageId: string | null,
): string | null {
  const parts: string[] = [];
  const existing = (existingReferences ?? "").trim();
  if (existing.length > 0) parts.push(existing);
  const id = (inboundMessageId ?? "").trim();
  if (id.length > 0 && !parts.join(" ").includes(id)) parts.push(id);
  const out = parts.join(" ").trim();
  return out.length > 0 ? out : null;
}

// Build the quoted-reply body that gets prefilled into the composer.
// Two blank lines (so the user can write above the quote), then a
// "On <date>, <from> wrote:" attribution, then each line of the
// inbound prefixed with "> ". Falls back to the snippet when no
// text/html body is available.
function buildQuotedReplyBody(m: GmailMessageDetail): string {
  const date = new Date(m.receivedAt);
  const dateLabel = Number.isFinite(date.getTime())
    ? date.toLocaleString()
    : m.receivedAt;
  const fromLabel = m.fromName
    ? `${m.fromName} <${m.fromAddress}>`
    : m.fromAddress;
  const raw = (() => {
    if (m.textBody && m.textBody.trim().length > 0) return m.textBody;
    if (m.htmlBody && m.htmlBody.trim().length > 0) {
      // Cheap HTML→text fallback so the quote stays readable.
      return m.htmlBody
        .replace(/<br\s*\/?\s*>/gi, "\n")
        .replace(/<\/p>/gi, "\n\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    }
    return m.snippet || "";
  })();
  const quoted = raw
    .split(/\r?\n/)
    .map((line) => `> ${line}`)
    .join("\n");
  return `\n\nOn ${dateLabel}, ${fromLabel} wrote:\n${quoted}`;
}

function GmailDetailFailedVerification({
  fromAddress,
  verificationStatus,
  verificationDetails,
}: {
  fromAddress: string;
  verificationStatus: VerificationStatus | null;
  verificationDetails: VerificationDetails | null;
}) {
  const { isTrusted, trustSender, isTrusting } = useTrustedSenderCheck(
    fromAddress,
    verificationStatus,
  );

  if (verificationStatus !== "fail" || isTrusted) return null;

  return (
    <span className="inline-flex items-center gap-1.5">
      <FailedVerificationWarning
        verificationStatus={verificationStatus}
        verificationDetails={verificationDetails}
        testIdSuffix="-gmail-detail"
      />
      <button
        onClick={trustSender}
        disabled={isTrusting}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-50"
        data-testid="gmail-detail-trust-sender"
      >
        <ShieldAlert className="h-3 w-3" />
        {isTrusting ? "Trusting…" : "Trust this sender"}
      </button>
    </span>
  );
}
